import React, { useState, useMemo } from "react";
import { useQuery, useMutation } from "urql";
import { sortBy, cloneDeep, groupBy, keyBy } from "lodash";
import { Dropdown } from "../../../../components/src/Dropdown";
import { Link } from "wouter";
import { Alert, AlertVariant } from "../../../../components/src/Alert";

import { YesNoModal } from "../../../../components/src/YesNoModal";
import { exportToXlsx, sheetType } from "../../../../components/src/common/exportToXlsx";
import { DateHelper } from "../../../../common/src";
import { DateForm } from "../../../../components/src/dateForm";

import { DELETE_NAV, GET_NAVS } from "../../../../graphql/src/nav-v2/queries";
import { Nav } from "../../types.generated";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { SelectColumnFilter } from "../../../../components/src/react-table/ReactTableFilters";
import { formikUrqlErrorFormater } from "../../../../components/src/common/formik-urql-error-helper";
import { useQueryState } from "../../../../components/src/use-query-state";

interface Props {
    navsData: any[];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    setModal: Function;
    refetch?: () => void;
}

const Navs = ({ setModal, navsData }: Props) => {
    const download = () => {
        const currentRecords: sheetType = navs;
        //console.log(currentRecords);
        exportToXlsx([currentRecords], "navs.xlsx");
    };

    let navs: any[] = cloneDeep(navsData);
    navs = sortBy(navs, "date");
    navs = navs.reverse();

    navs.forEach((nav) => {
        nav.clientName = nav.client.name;
        nav.updatedBy = nav.updateUserInfo.name;
        nav.canDelete = false;
    });

    const navsByClientId = groupBy(navs, (nav) => {
        return nav.clientId;
    });

    Object.keys(navsByClientId).map((clientId) => {
        // this is embarrassingly inefficient but I am in a hurry
        let l: any[] = navsByClientId[clientId];
        l = sortBy(l, "date");
        l = l.reverse();
        l = l.filter((item) => item.status !== "Deleted");
        // delete is allowed for last 2 records only
        l.slice(0, 2).forEach((n) => {
            n.canDelete = true;
        });
    });

    // https://github.com/TanStack/table/issues/4241 does not seem to work now
    // const columnHelper = createColumnHelper<GetNavsQuery["navs"]>();
    /*
            columnHelper.accessor("_id", {
                header: "Id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Link to={`/nav2/${row.original._id}`}>
                            <div>#</div>
                        </Link>
                    );
                },
                size: 1
            }),
    */

    const columns = useMemo(
        () => [
            {
                accessorKey: "_id",
                header: "Id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Link to={`/nav2/${row.original._id}`}>
                            <div>#</div>
                        </Link>
                    );
                },
                size: 1
            },
            {
                accessorKey: "clientName",
                header: "Client",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/parties/${row.original.clientId}`}>{cellProps.getValue()}</Link>;
                }
            },
            {
                accessorKey: "date",
                header: "Date",
                cell: (cellProps) => {
                    return <div className="text-center">{cellProps.getValue()}</div>;
                }
            },
            {
                accessorKey: "createTimestamp",
                header: "Create timestamp",
                cell: (cellProps) => {
                    return <div className="text-center">{cellProps.getValue()}</div>;
                }
            },
            {
                accessorKey: "updatedBy",
                header: "Updated by"
            },
            {
                accessorKey: "status",
                header: "Status",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    const value = cellProps.getValue();
                    if (value !== "Deleted" && row.original.canDelete) {
                        return (
                            <Dropdown>
                                <Dropdown.Toggle variant="link" as="a" size="xs" bsPrefix="m-dropdown__toggle" id={row.original._id}>
                                    {value}
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {["Deleted"].map((changeTo, i) => {
                                        return (
                                            <Dropdown.Item
                                                key={i}
                                                onClick={async () => {
                                                    if (changeTo === "Deleted") {
                                                        setModal({
                                                            showModal: true,
                                                            payload: {
                                                                _id: row.original._id,
                                                                publishedToKeys:
                                                                    row.original.publishedTo && row.original.publishedTo.length
                                                                        ? Object.keys(keyBy(row.original.publishedTo, "key"))
                                                                        : null
                                                            }
                                                        });
                                                    }
                                                }}
                                            >
                                                Change to {changeTo}
                                            </Dropdown.Item>
                                        );
                                    })}
                                </Dropdown.Menu>
                            </Dropdown>
                        );
                    } else {
                        return <div>{value}</div>;
                    }
                }
            }
        ],
        [setModal]
    );

    return (
        <div>
            <ReactTable columns={columns} data={navs} defaultHiddenColumns={[]} onExport={download} />
        </div>
    );
};

export function NavsPage(): React.ReactElement {
    const [startDate] = useQueryState("startDate", DateHelper.addDays(new Date(), -20).toISOString().slice(0, 10));

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    const [{ fetching: loading, error, data }, refetch] = useQuery<{ navs?: Partial<Nav>[] }>({
        query: GET_NAVS,
        variables: { startDate: startDate },
        requestPolicy: "cache-and-network"
    });

    const [_, deleteNavFn] = useMutation(DELETE_NAV);

    const [modal, setModal] = useState({ showModal: false, payload: null });

    if (error)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (loading)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={
                        modal.payload.publishedToKeys
                            ? "Are you sure you want to delete NAV with id " +
                              modal.payload._id +
                              "? " +
                              "\n\n" +
                              "WARNING" +
                              "\n" +
                              "NAV reports has been sent, make sure to contact the following recipients of deleted nav to inform them about the change:\n- " +
                              modal.payload.publishedToKeys.join("\n- ")
                            : "Are you sure you want to delete NAV with id " + modal.payload._id + "? "
                    }
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        const variables: { _id: string } = modal.payload;
                        deleteNavFn(variables)
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    const message = formikUrqlErrorFormater(result.error);
                                    setAlert({ color: "danger", visible: true, message });
                                } else {
                                    refetch();
                                }
                                return true;
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            });
                    }}
                />
            ) : null}
            {alert.visible ? (
                <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                    {alert.message}
                </Alert>
            ) : null}
            <div className="flex mb-2">
                <DateForm defaultDateString={startDate} label="Start date" dateName={"startDate"}></DateForm>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <Navs setModal={setModal} navsData={data.navs} />
                </div>
            </div>
        </div>
    );
}
